import React, { useContext, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useSelector } from 'react-redux'
import useAppView from 'Core/Hooks/useAppView'
import { FeatureFlagContext } from 'Core/FeatureFlagContext'
import Loader from 'Components/Loader'
import { useHistory } from 'react-router'
import { enqueueNotification } from 'Utils/Helpers'
import { AppHeader, Typography } from 'procyon-ui'
import { useUser } from 'Core/Hooks/useUser'
// Leaf-component-only module — the full V2Components barrel pulls graph
// components whose feature imports would drag features into the eager bundle.
import { LoadingFeedback, PingOneLogo, PingPrivilegeLogo } from 'V2Components/ShellExports'
import axios from 'axios'
import localforage from 'localforage'
import { getHTMLDataTestID } from 'infra/utils'
import { useLayoutContext } from 'features/layout'

const useStyles = makeStyles((theme) => ({
  root: {},
  appHeaderWrapper: {
    position: 'fixed',
    top: 0,
    width: '100vw',
    zIndex: 1500,
    borderBottom: 'var(--border-width) solid var(--color-border-base)',
    backgroundColor: 'var(--color-bg-base)'
  },
  logo: {
    '& img': {
      width: '125px'
    }
  }
}))

function Header() {
  const classes = useStyles()
  const history = useHistory()
  const { viewMode } = useAppView()
  const nextViewMode = viewMode === 'admin' ? 'user' : 'admin'
  const { updateContext: asyncChangeAppView } = useContext(FeatureFlagContext)
  const { isAgentlessMode } = useUser()
  const [orgChanging, setOrgChanging] = useState(false)
  const [appViewChanging, setAppViewChanging] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const { whoAmI } = useLayoutContext()
  const user = useSelector((state) => state.user)
  const envName = whoAmI?.tenantname

  const copyID = (ID) => {
    try {
      navigator.clipboard.writeText(ID)
      enqueueNotification('Derived ID Copied!', 'info')
    } catch (error) {
      // ignore
    }
  }

  const switchViewMode = async () => {
    setAppViewChanging(true)
    await asyncChangeAppView(nextViewMode)
    setAppViewChanging(false)
  }

  const logoutUser = async () => {
    setLoggingOut(true)
    try {
      const hostName = window.location.host
      const updatedHost = hostName.replace('console.', '')
      const logoutURL = `https://console.login.${updatedHost}/logout`

      const response = await axios.get(logoutURL, {
        withCredentials: true
      })
      if (response && response.data) {
        // clear all the cookies
        document.cookie.split(';').forEach((cookie) => {
          const name = cookie.split('=')[0].trim()
          document.cookie = `${name}=; max-age=0; path=/;`
        })

        // clear localStorage too
        localStorage.clear()

        // Clear IndexedDB too
        if (window.indexedDB && window.indexedDB.databases) {
          try {
            const dbs = await window.indexedDB.databases()
            for (const db of dbs) {
              if (db.name) window.indexedDB.deleteDatabase(db.name)
            }
          } catch (e) {
            console.error('Failed to clear IndexedDB databases', e)
          }
        } else {
          // fallback to localforage.clear() if databases() is not supported
          try {
            await localforage.clear()
          } catch (e) {
            console.error('Failed to clear localforage', e)
          }
        }
        window.location.href = response.data
      } else {
        console.error('Logout failed. Empty or invalid response.')
      }
    } catch (error) {
      console.error('Logout error:', error)
      setLoggingOut(false)
    }
  }

  const getHeaderMenuItems = () => {
    const items = []
    const isAdmin = whoAmI ? whoAmI.isadmin : user?.role === 'admin'

    if (isAdmin) {
      if (viewMode === 'admin') {
        items.push({
          action: () => history.push('/admin/settings'),
          title: 'Settings',
          dataTestId: getHTMLDataTestID('header', 'div', 'settings')
        })
      }

      items.push({
        action: switchViewMode,
        title: `Switch to ${nextViewMode}`,
        dataTestId: getHTMLDataTestID('header', 'div', 'switch-view-mode')
      })
    }

    if (viewMode === 'user') {
      if (user?.Spec?.AzureDerivedID) {
        items.push({
          action: () => copyID(user?.Spec?.AzureDerivedID),
          title: `Copy Azure Derived ID`
        })
      }
    }

    if (isAgentlessMode) {
      items.push({
        action: logoutUser,
        title: 'Logout'
      })
    }

    return items
  }

  return (
    <div className={classes.root}>
      <div className={classes.appHeaderWrapper}>
        <AppHeader
          legacy={false}
          showNotificationBell={false}
          dropDownMenuItems={[...getHeaderMenuItems()]}
          BrandLogo={
            <div className='flex items-center justify-between gap-4'>
              <PingOneLogo className='!w-[150px]' />
              <PingPrivilegeLogo className='!w-[150px] mt-1' />
            </div>
          }
          className={classes.logo}
          userName={user?.Spec?.UserName ? user.Spec.UserName : user?.Spec?.ServerUserName}
          onClickNotificationButton={function noRefCheck() {}}
          userRole={
            <div className='flex items-center gap-1'>
              <Typography className='!text-gray-700' variant='bodySmall-regular'>
                <span className='capitalize'>{viewMode}</span>@
                <strong>{envName ?? '...'}</strong>
              </Typography>
            </div>
          }
        />
      </div>

      <LoadingFeedback
        loading={orgChanging}
        caption='Please wait'
        message='Changing Organization'
      />
      <LoadingFeedback loading={loggingOut} caption='Please wait' message='Logging Off' />
      <Loader loading={appViewChanging} />
    </div>
  )
}

export { Header }
